<script>
import { ref } from 'vue';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useCaptain } from 'dashboard/composables/useCaptain';
import { useTrack } from 'dashboard/composables';
import { vOnClickOutside } from '@vueuse/components';
import { REPLY_EDITOR_MODES, CHAR_LENGTH_WARNING } from './constants';
import { CAPTAIN_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import EditorModeToggle from './EditorModeToggle.vue';
import CopilotMenuBar from './CopilotMenuBar.vue';

export default {
  name: 'ReplyTopPanel',
  components: {
    EditorModeToggle,
    CopilotMenuBar,
  },
  directives: {
    OnClickOutside: vOnClickOutside,
  },
  props: {
    mode: {
      type: String,
      default: REPLY_EDITOR_MODES.REPLY,
    },
    isReplyRestricted: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isEditorDisabled: {
      type: Boolean,
      default: false,
    },
    conversationId: {
      type: Number,
      default: null,
    },
    isMessageLengthReachingThreshold: {
      type: Boolean,
      default: () => false,
    },
    charactersRemaining: {
      type: Number,
      default: () => 0,
    },
    editorContent: {
      type: String,
      default: undefined,
    },
    showFollowUp: {
      type: Boolean,
      default: false,
    },
    showForwardContact: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'setReplyMode',
    'executeCopilotAction',
    'openFollowUpModal',
    'openForwardContactModal',
    'openAssinatura',
  ],
  setup(props, { emit }) {
    const setReplyMode = mode => {
      emit('setReplyMode', mode);
    };
    const handleReplyClick = () => {
      if (props.isReplyRestricted) return;
      setReplyMode(REPLY_EDITOR_MODES.REPLY);
    };
    const handleNoteClick = () => {
      setReplyMode(REPLY_EDITOR_MODES.NOTE);
    };
    const handleModeToggle = () => {
      const newMode =
        props.mode === REPLY_EDITOR_MODES.REPLY
          ? REPLY_EDITOR_MODES.NOTE
          : REPLY_EDITOR_MODES.REPLY;
      setReplyMode(newMode);
    };

    const { captainTasksEnabled } = useCaptain();
    const showCopilotMenu = ref(false);

    const handleCopilotAction = (actionKey, data) => {
      emit('executeCopilotAction', actionKey, data || props.editorContent);
      showCopilotMenu.value = false;
    };

    const toggleCopilotMenu = () => {
      const isOpening = !showCopilotMenu.value;
      if (isOpening) {
        useTrack(CAPTAIN_EVENTS.EDITOR_AI_MENU_OPENED, {
          conversationId: props.conversationId,
          entryPoint: 'top_panel',
        });
      }
      showCopilotMenu.value = isOpening;
    };

    const handleClickOutside = () => {
      showCopilotMenu.value = false;
    };

    const keyboardEvents = {
      'Alt+KeyP': {
        action: () => handleNoteClick(),
        allowOnFocusedInput: true,
      },
      'Alt+KeyL': {
        action: () => handleReplyClick(),
        allowOnFocusedInput: true,
      },
    };
    useKeyboardEvents(keyboardEvents);

    return {
      handleModeToggle,
      handleReplyClick,
      handleNoteClick,
      REPLY_EDITOR_MODES,
      captainTasksEnabled,
      handleCopilotAction,
      showCopilotMenu,
      toggleCopilotMenu,
      handleClickOutside,
    };
  },
  computed: {
    replyButtonClass() {
      return {
        'is-active': this.mode === REPLY_EDITOR_MODES.REPLY,
      };
    },
    noteButtonClass() {
      return {
        'is-active': this.mode === REPLY_EDITOR_MODES.NOTE,
      };
    },
    charLengthClass() {
      return this.charactersRemaining < 0 ? 'text-n-ruby-9' : 'text-n-slate-11';
    },
    characterLengthWarning() {
      return this.charactersRemaining < 0
        ? `${-this.charactersRemaining} ${CHAR_LENGTH_WARNING.NEGATIVE}`
        : `${this.charactersRemaining} ${CHAR_LENGTH_WARNING.UNDER_50}`;
    },
  },
};
</script>

<template>
  <div
    class="rtp-linha flex justify-between gap-2 h-[3.25rem] items-center ltr:pl-3 ltr:pr-2 rtl:pr-3 rtl:pl-2"
  >
    <EditorModeToggle
      :mode="mode"
      :disabled="disabled"
      :is-reply-restricted="isReplyRestricted"
      @toggle-mode="handleModeToggle"
    />
    <div class="rtp-espaco flex items-center mx-4 my-0">
      <div v-if="isMessageLengthReachingThreshold" class="text-xs">
        <span :class="charLengthClass">
          {{ characterLengthWarning }}
        </span>
      </div>
    </div>
    <div class="flex items-center gap-1">
      <!-- A peça é `.advup-acao--tingida`, a mesma do alto da conversa: cor por
           SIGNIFICADO (âmbar é prazo, teal é pessoa, íris é IA). Era o botão
           genérico com classes por cima e `!important` para vencer o fundo
           dele. -->
      <button
        v-if="showFollowUp"
        v-tooltip.top="$t('CONVERSATION.FOOTER.FOLLOW_UP')"
        type="button"
        class="advup-acao advup-acao--tingida advup-acao--t-ambar"
        :aria-label="$t('CONVERSATION.FOOTER.FOLLOW_UP')"
        @click="$emit('openFollowUpModal')"
      >
        <i class="i-ph-calendar" />
      </button>
      <button
        v-if="showForwardContact"
        v-tooltip.top="$t('CONVERSATION.FOOTER.FORWARD_CONTACT')"
        type="button"
        class="advup-acao advup-acao--tingida advup-acao--t-teal"
        :aria-label="$t('CONVERSATION.FOOTER.FORWARD_CONTACT')"
        @click="$emit('openForwardContactModal')"
      >
        <i class="i-ph-address-book" />
      </button>
      <!-- Minutas: mandar documento para o cliente assinar AQUI, pela
           conversa. Azul = o que está andando (a marca). -->
      <button
        v-if="showForwardContact"
        v-tooltip.top="$t('ASSINATURAS.BOTAO_DICA')"
        type="button"
        class="advup-acao advup-acao--tingida advup-acao--t-azul"
        :aria-label="$t('ASSINATURAS.BOTAO_DICA')"
        @click="$emit('openAssinatura')"
      >
        <i class="i-ph-signature" />
      </button>
      <div v-if="captainTasksEnabled" class="flex items-center gap-1">
        <div class="relative">
          <button
            v-tooltip.top="$t('CONVERSATION.FOOTER.COPILOT')"
            type="button"
            class="advup-acao advup-acao--tingida advup-acao--t-iris"
            :class="{ 'advup-acao--aceso': showCopilotMenu }"
            :aria-label="$t('CONVERSATION.FOOTER.COPILOT')"
            :aria-expanded="showCopilotMenu"
            :disabled="disabled || isEditorDisabled"
            @click="toggleCopilotMenu"
          >
            <i class="i-lucide-sparkles" />
          </button>
          <CopilotMenuBar
            v-if="showCopilotMenu"
            v-on-click-outside="handleClickOutside"
            :has-selection="false"
            :editor-content="editorContent"
            :conversation-id="conversationId"
            class="ltr:right-0 rtl:left-0 bottom-full mb-2"
            @execute-copilot-action="handleCopilotAction"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* ── Celular ─────────────────────────────────────────────────────────────────
   Medido em 08/09/2026 em 375px: o último botão desta fileira (encaminhar
   contato) começava em x=371 e terminava em 403 — 28px FORA da tela. Ou seja,
   uma ação existia e não dava para tocar nela.

   A causa é o espaçador `mx-4` (32px) entre o seletor de modo e as ações, que
   faz sentido numa fileira larga e não numa de 375px. No celular ele some e a
   altura fixa cede, para as ações caberem sempre. Acima de 640px nada muda. */
@media (max-width: 639px) {
  .rtp-linha {
    height: auto;
    min-height: 3.25rem;
    flex-wrap: wrap;
    row-gap: var(--esp-1);
  }

  .rtp-espaco {
    margin-left: 0;
    margin-right: 0;
  }
}
</style>
